import { excludeProps } from '@lokalise/styled';
import type { ReactNode } from 'react';

import InputField from '@app/components/form/InputField';
import SelectField from '@app/components/form/SelectField';
import type { SelectFieldProps } from '@app/features/auth/components/Select/Select';
import styled, { createGlobalStyle, css } from '@app/styled';
import { color, font, mediaQuery, modifier, spacing, typography } from '@app/theme';
import {
    Button as LouisButton,
    Checkbox as LouisCheckbox,
    Input as LouisInput,
    Label as LouisLabel,
} from '@lokalise/louis';
import type { GroupBase } from 'react-select';

export const Layout = styled.div.withConfig({ shouldForwardProp: excludeProps(['narrow', 'nested', 'padding']) })<{
    narrow?: boolean;
    nested?: boolean;
    padding?: boolean;
}>`
    display: flex;
    font-size: 15px;
    width: 945px;
    justify-content: space-between;
    margin: 70px auto 130px;
    font-family: ${font('family.heading')};

    ${mediaQuery.max('md')} {
        max-width: 750px;
        width: auto;
    }

    ${modifier(
        'narrow',
        css`
            width: 565px;
            justify-content: space-around;
        `,
    )}
    ${modifier(
        'nested',
        css`
            margin: 30px auto;
        `,
    )}
    ${modifier(
        'padding',
        css`
            padding: 0 16px;
        `,
    )}
`;
export const InfoLayout = styled.div`
    display: flex;
    font-size: 15px;
    width: 1140px;
    justify-content: space-between;
    margin: 50px auto;
    padding: 20px;

    ${mediaQuery.max('md')} {
        max-width: 750px;
        width: auto;
        flex-direction: column;
    }
`;
export const InfoLayoutContent = styled.div`
    margin-left: 140px;
    flex-basis: 100%;

    ${mediaQuery.max('md')} {
        margin: 30px 0 0 0;
    }
`;
export const SSOLayout = styled.div`
    margin: 50px auto 130px;
    width: 565px;
`;
export const Column = styled.div.withConfig({
    shouldForwardProp: (prop) => !['hidden'].includes(prop),
})<{ hidden?: boolean }>`
    flex-basis: 50%;
    padding: 0 15px;

    ${mediaQuery.max('md')} {
        flex-basis: 100%;
        ${modifier(
            'hidden',
            css`
                display: none;
            `,
        )}
    }
`;
export const Illustration = styled.img`
    width: 360px;
    padding-top: 70px;
`;

export const AuthHeading = css`
    font-family: ${font('family.heading')};
    font-weight: 700;
`;

interface ButtonProps {
    loading?: boolean;
    loadingText?: string;
    outlined?: boolean;
    onClick?: () => void;
    children?: ReactNode;
    target?: '_blank' | '_self' | '_parent' | '_top' | 'framename';
    href?: string;
}

export const StyledButton = styled.button.withConfig({
    shouldForwardProp: (prop) => !['loading', 'outlined'].includes(prop),
})<ButtonProps>`
    width: 100%;
    background-color: ${color('background.action.primary.default')};
    color: ${color('text.inverse')};
    border: 1px solid ${color('background.action.primary.default')};
    height: 45px;
    line-height: 43px;
    font-size: 15px;
    font-weight: 600;
    padding: 0 20px;
    text-align: center;
    cursor: pointer;
    display: inline-block;
    transition: background-color 0.2s, border-color 0.2s;

    &:hover:enabled {
        background-color: ${color('background.action.primary.hover')};
        border-color: ${color('background.action.primary.hover')};
    }

    &:disabled {
        cursor: not-allowed;
    }

    ${modifier(
        'loading',
        css`
            opacity: 0.65;

            &:after {
                content: '...';
            }
        `,
    )}

    ${modifier(
        'outlined',
        css`
            color: ${color('text.default')};
            background-color: ${color('background.surface.default')};
            border-color: ${color('border.default')};

            &:hover:enabled {
                background-color: ${color('background.surface.default')};
                border-color: ${color('background.action.primary.default')};
                color: ${color('text.default')};
            }
        `,
    )}
    
    &:disabled {
        background: #ffc1af;
        border-color: transparent;
        cursor: not-allowed;
        pointer-events: none;
    }
`;

export const Button = styled(LouisButton).withConfig({
    shouldForwardProp: (prop) => !['outlined'].includes(prop),
})<{ outlined?: boolean }>`
    // Increase specificity to override parent styles
    && {
        border-radius: 25px;
        width: 100%;
        font-size: 16px;
        height: 40px;
        ${AuthHeading};

        ${modifier(
            'outlined',
            css`
                color: ${color('custom.content.black')};
                background-color: ${color('custom.content.white')};
                border-color: ${color('custom.content.black')};

                &:hover:enabled {
                    background-color: ${color('custom.content.white')};
                    border-color: ${color('custom.interactive.primary.idle')};
                    color: ${color('custom.content.black')};
                }
            `,
        )}
    }
`;

type InputProps = {
    search?: boolean;
};

export const Input = styled.input<InputProps>`
    border: 1px solid rgba(0, 0, 0, 0.3);
    height: 45px;
    line-height: 20px;
    display: block;
    width: 100%;
    font-size: 15px;
    color: ${color('text.default')};
    padding: 0 20px 2px;
    border-radius: 0;

    &:focus {
        border-color: ${color('border.input.hover')};
    }

    &:hover {
        border-color: ${color('border.input.hover')};
    }

    &:disabled {
        color: ${color('text.disabled')};
        background-color: ${color('background.disabled')};
        cursor: not-allowed;
    }

    ${modifier(
        'search',
        css`
            padding-left: 40px;
        `,
    )}
`;

export const AuthInput = styled(LouisInput)`
    & > * {
        height: 40px;
        border-radius: 8px;
    }
`;

export const AuthLabel = styled(LouisLabel)`
    & > * {
        ${AuthHeading}
    }
`;

export const AuthCheckbox = styled(LouisCheckbox)`
    & label {
        & > * {
            font-family: ${font('family.heading')};
        }
    }
`;

export const AuthInputField = styled(InputField)`
    & > :first-child {
        height: 40px;
        border-radius: 8px;
    }
`;

export const Select = styled.select`
    border: 1px solid rgba(0, 0, 0, 0.3);
    height: 45px;
    line-height: 20px;
    display: block;
    width: 100%;
    font-size: 15px;
    color: ${color('text.default')};
    padding: 0 20px 2px;
    border-radius: 0;
    background-position: right 50%;
    background-image: url('data:image/svg+xml;utf8,<?xml version="1.0" encoding="utf-8"?><!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" version="1"><path d="M4 8L0 4h8z"/></svg>');
    background-repeat: no-repeat;
    appearance: none;

    &:focus {
        border-color: ${color('border.input.hover')};
    }

    &:hover {
        border-color: ${color('border.input.hover')};
    }

    &[data-selected=''] {
        color: ${color('text.light.default')};
    }

    &:disabled {
        opacity: 1;
        color: ${color('text.disabled')};
        background-color: ${color('background.input.readonly')};
        cursor: not-allowed;
    }
`;

type Async = boolean;
type Creatable = boolean;
type OptionType = any;
type IsMulti = boolean;
type GroupType = GroupBase<OptionType>;

type AuthSelectProps = SelectFieldProps<Async, Creatable, OptionType, IsMulti, GroupType>;

export const AuthSelectField = styled(SelectField)<AuthSelectProps>`
    .Select__control {
        min-height: 40px;
        border-radius: 8px;
    }
`;

export const Label = styled.label<{ required?: boolean }>`
    font-size: 15px;
    color: ${color('text.default')};
    font-weight: 700;
    margin-bottom: 0;

    ${modifier(
        'required',
        css`
            &:after {
                content: ' *';
                color: #ef7b62;
            }
        `,
    )}
`;

export const Header = styled.h1.withConfig({
    shouldForwardProp: excludeProps(['center']),
})<{ center?: boolean }>`
    font-size: 30px;
    margin-bottom: 30px;
    color: ${color('text.default')};

    ${modifier(
        'center',
        css`
            text-align: center;
        `,
    )}
`;
export const SubHeader = styled.h2`
    color: #777777;
    font-weight: 400;
    font-size: 20px;
    margin: -20px 0 40px;
`;

export const Text = styled.div`
    line-height: 1.42857143;
`;

export const Separator = styled.div`
    text-align: center;
    display: flex;
    align-items: center;
    font-size: 12px;
    color: ${color('text.default')};
    margin: 35px 0 20px;

    &:before {
        content: '';
        height: 1px;
        display: block;
        background-color: ${color('text.default')};
        width: auto;
        flex-grow: 1;
        margin-right: 30px;
    }

    &:after {
        margin-left: 30px;
        flex-grow: 1;
        content: '';
        height: 1px;
        display: block;
        background-color: ${color('text.default')};
        width: auto;
    }
`;

export const Link = styled.a<{ $bold?: boolean }>`
    /* TODO: remove !important, needs to rewrite lokalise global css included in base layout */
    text-decoration: underline !important;
    color: ${color('text.default')};
    cursor: pointer;
    transition: color 0.2s;

    ${modifier(
        '$bold',
        css`
            font-weight: bold;
        `,
    )}
    &:hover {
        text-decoration: underline;
        color: ${color('text.link.hover')};
    }
`;

export const LinkButton = styled.button<{ bold?: boolean }>`
    border: none;
    background: transparent;
    text-decoration: underline;
    color: ${color('text.link.default')};
    cursor: pointer;
    padding: 0;
    ${modifier(
        'bold',
        css`
            font-weight: bold;
        `,
    )}
`;

export const Small = styled.small`
    font-size: 12px;
`;

export const Error = styled.div`
    color: darkred;
    margin: 10px 0;
    font-size: 14px;
`;

export const CommonError = styled.div`
    margin-bottom: 20px;
    font-size: 14px;
`;

export const PolicyItemWrapper = styled.div`
    color: ${color('text.default')};
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 12px;
    gap: 4px;
`;

export const PolicyItemIcon = styled.div`
    flex-basis: 25px;
    position: relative;
    font-size: 24px;
`;

export const SocialButtons = styled.div`
    display: flex;
    justify-content: space-around;
    position: relative;
    margin-top: 40px;
`;
export const SocialButtonsWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 145px;
    justify-content: space-between;
`;
export const SocialButtonsLabelWrapper = styled.div`
    position: absolute;
    left: 0;
    top: 10px;
`;

export const FormRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    margin-bottom: ${spacing(6)};
    width: 100%;
`;

export const FormRowB = styled.div`
    width: 410px;
    display: flex;
    gap: ${spacing(2)};
    flex-direction: column;

    ${mediaQuery.max('sm')} {
        width: 100%;
    }
`;

export const EmailPageWrapper = styled.div`
    width: 410px;

    ${mediaQuery.max('sm')} {
        width: 100%;
    }
`;

export const ButtonWrapper = styled.div`
    margin: 30px 0 20px;
`;
export const FooterLinks = styled.div<{ $center?: boolean }>`
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    font-size: 15px;

    ${modifier(
        '$center',
        css`
            justify-content: space-around;
        `,
    )};
`;
export const StyledPolicyList = styled.div`
    margin: 20px 0;
`;
export const IconButton = styled.button.attrs({
    type: 'button',
})`
    display: inline-block;
    margin: 0;
    padding: 0;
    border: none;
    background-color: transparent;
`;

export const AuthIconButton = styled(LouisButton)`
    // Increase specificity to override parent styles
    && {
        border: solid 1px ${color('custom.interactive.content.idle')};
        border-radius: 25px;
        width: 100%;
        font-family: ${font('family.heading')};
        font-size: 15px;
        font-weight: 700;
        color: #262626f5;
        height: 40px;
    }
`;

export const BodyStyle = createGlobalStyle`
    body {
        background: linear-gradient(to right, white 50%, #ffebe5 50%);

        ${mediaQuery.max('md')} {
            background: white;
        }
    }
`;

export const Page = styled.div``;
export const LayoutB = styled.div`
    width: 1045px;
    display: grid;
    margin: 8vh auto;
    grid-template-columns: 420px 420px;
    justify-content: space-between;
    color: ${color('text.default')};

    ${mediaQuery.max('md')} {
        width: auto;
        justify-content: space-around;
        grid-template-columns: 1fr;
        grid-template-rows: auto auto;
        margin: 7px auto 0;
    }

    ${mediaQuery.max('lg')} {
        width: auto;
        justify-content: space-around;
    }
`;

export const PageHeader = styled.div`
    margin: 0 auto;
    padding: 33px 15px;
    width: 1320px;
`;

export const SignupHeader = styled.h1`
    text-align: left;
    color: ${color('text.default')};
    margin-bottom: ${spacing(2)};
    ${typography('custom.heading1')};
    ${AuthHeading};

    ${mediaQuery.max('sm')} {
        ${typography('custom.heading3')}
    }
`;

export const SignupSubHeader = styled.div`
    font-size: 15px;
    text-align: left;
    color: ${color('text.default')};
    font-family: ${font('family.inter')};
`;

export const SignupText = styled.div`
    font-weight: 600;
    font-size: 18px;
    line-height: 25px;
    text-align: center;
    color: ${color('text.default')};
    margin-bottom: 25px;
`;
export const SignupColumn = styled.div`
    width: 386px;
    text-align: center;
    padding: 0 15px;

    img {
        margin-bottom: 47px;
    }

    ${mediaQuery.max('md')} {
        background-color: #ffebe5;
        width: 100%;
        padding: 60px 15px;
        margin-top: 40px;
    }
`;
export const RatingBlock = styled.div`
    display: flex;
    justify-content: space-evenly;
`;

export const Error2 = styled.div`
    color: darkred;
    font-size: 14px;
`;

export const Label2 = styled.label<{ required?: boolean }>`
    font-size: 15px;
    font-weight: 600;
    color: ${color('text.default')};

    ${modifier(
        'required',
        css`
            &:after {
                content: ' *';
                color: ${color('background.action.primary.default')};
            }
        `,
    )}
`;

export const withInputErrorModifier = modifier(
    'error',
    css`
        border-color: ${color('border.danger')};
    `,
);
